/**
 * O RECIBO do colaborador — "o que eu recebi, e por quê", num texto que cabe
 * numa mensagem de WhatsApp.
 *
 * Dois calendários, dois recibos, pela mesma razão que o Financeiro tem duas
 * abas (v3.1): o DIARISTA é pago por quinzena e o MENSALISTA/encarregado pelo
 * fechamento do mês. Um recibo só, misturando os dois ritmos, seria a mesma
 * confusão que a tela deixou de ter.
 *
 * ⚠️ Este arquivo NÃO calcula dinheiro novo. Ele SOMA e ESCREVE o que as
 * funções canônicas já decidiram: `calcFormula` (folha do diarista),
 * `netLancamentosCentavos` (lançamentos), `salarioLinhaMes`/`liquidoLinhaMes`
 * (mês, snapshot quando existe). Uma segunda conta aqui seria um número que
 * discorda da tela — e o colaborador com o papel na mão é a última pessoa que
 * deveria descobrir isso.
 *
 * ⚠️ Texto BMP-only. Emoji fora do BMP vira ◆ no WhatsApp Desktop; os
 * marcadores são os mesmos do Plano do Campo: ▸ • —
 */

import {
  formatarBRL, formatarDataCurta, formatarCompetencia, paraCentavos, paraReais,
  netLancamentosCentavos, agruparLancamentosPorPagamento, textoPagamentoLancamentos,
  salarioLinhaMes, liquidoLinhaMes, adiantamentoLinhaMesCentavos,
  situacaoAdiantamentoMes, estadoLinhaMes,
  type LinhaMensalistaMes, type LancamentoDoMes,
} from "./equipe-fechamento.js";
import { calcFormula, TIPOS_TRABALHO } from "./equipe-formula.js";

const MARCA_SECAO = "▸"; // ▸
const MARCA_ITEM = "•";  // •

/** Rótulo de tela do tipo da diária — o mesmo vocabulário do Financeiro. */
const TIPO_LABEL: Record<string, string> = {
  NORMAL: "Diária",
  MEIA_DIARIA: "Meia diária",
  HORA_EXTRA: "Hora extra",
  EMPREITA: "Empreita",
  DESCONTO: "Desconto",
  ADIANTAMENTO: "Adiantamento",
};

function nomeDe(p: { nome: string; apelido?: string | null }): string {
  return p.apelido ?? p.nome;
}

function plural(n: number, singular: string, pluralForm: string): string {
  return `${n} ${n === 1 ? singular : pluralForm}`;
}

/**
 * "sáb 12/09/2026" — num RECIBO o ano importa: o papel fica guardado e é
 * consultado meses depois, quando "12/09" sozinho já não diz de qual ano.
 * Na tela o ano continua fora (`formatarDataCurta`), onde o contexto está à vista.
 */
function dataComAno(iso: string | null | undefined): string {
  if (!iso) return "—";
  return `${formatarDataCurta(iso)}/${iso.slice(0, 4)}`;
}

/** "1,5" — dias com meia diária não podem virar "1.5" nem sumir. */
function formatarDias(dias: number): string {
  return Number.isInteger(dias) ? String(dias) : dias.toFixed(1).replace(".", ",");
}

export interface ReciboPessoa {
  nome: string;
  apelido?: string | null;
}

export interface ReciboDiaria {
  data: string; // 'YYYY-MM-DD'
  tipo: string;
  valor: string | number;
  /** Atividades do dia, quando a tela souber ("Colheita, Adubação"). */
  descricao?: string | null;
}

export interface ReciboLancamento {
  data: string;
  sinal?: string | null;
  descricao?: string | null;
  valor: string | number;
}

export interface ReciboQuinzenaArgs {
  organizacao: string;
  pessoa: ReciboPessoa;
  quinzena: {
    periodoInicio: string;
    periodoFim: string;
    dataPagamento: string | null;
    status: string; // ABERTA | FECHADA | PAGA
  };
  /** Diárias da pessoa NESTA quinzena, já filtradas por funcionário. */
  diarias: ReciboDiaria[];
  /** Lançamentos que saem NESTA quinzena (já passados por `lancamentosNoCaixaDaQuinzena`). */
  lancamentos: ReciboLancamento[];
  /** Data civil de hoje na fazenda — nunca `new Date()` aqui dentro. */
  geradoEm: string;
}

/**
 * Recibo do DIARISTA: o que sai nesta quinzena, dia a dia.
 *
 * Só se chama RECIBO quando a quinzena está PAGA. Antes disso é
 * DEMONSTRATIVO, e o texto diz que ainda não foi pago — mandar "recibo" de
 * dinheiro que não saiu é a mesma mentira que o "já pago nas quinzenas" sobre
 * valor previsto.
 */
export function montarReciboQuinzena(args: ReciboQuinzenaArgs): string {
  const { bruto, descontos, adiantamentos, liquido, dias } = calcFormula(
    args.diarias.map(d => ({ tipo: d.tipo, valor: d.valor })),
  );
  const lancCentavos = netLancamentosCentavos(args.lancamentos);
  const totalCentavos = Math.round(liquido * 100) + lancCentavos;

  const pago = args.quinzena.status === "PAGA";
  const linhas: string[] = [];

  linhas.push(`${pago ? "RECIBO" : "DEMONSTRATIVO"} ${MARCA_ITEM} ${nomeDe(args.pessoa)}`);
  linhas.push(`Quinzena ${formatarDataCurta(args.quinzena.periodoInicio)} a ${dataComAno(args.quinzena.periodoFim)}`);
  if (args.quinzena.dataPagamento) {
    linhas.push(pago
      ? `Pago em ${dataComAno(args.quinzena.dataPagamento)}`
      : `A pagar em ${dataComAno(args.quinzena.dataPagamento)}`);
  } else {
    linhas.push("Data de pagamento ainda não definida");
  }
  linhas.push("");

  // ─── Diárias ───────────────────────────────────────────────────────────────
  const trabalho = args.diarias.filter(d => (TIPOS_TRABALHO as readonly string[]).includes(d.tipo));
  if (trabalho.length > 0) {
    linhas.push(`${MARCA_SECAO} Diárias ${MARCA_ITEM} ${formatarBRL(bruto)} (${formatarDias(dias)} ${dias === 1 ? "dia" : "dias"})`);
    for (const d of [...trabalho].sort((a, b) => a.data.localeCompare(b.data))) {
      const rotulo = d.descricao?.trim() || TIPO_LABEL[d.tipo] || d.tipo;
      linhas.push(`  ${MARCA_ITEM} ${formatarDataCurta(d.data)} — ${rotulo} — ${formatarBRL(d.valor)}`);
    }
    linhas.push("");
  }

  // ─── Descontos e adiantamentos LANÇADOS COMO DIÁRIA (abatem no líquido) ────
  const abatidos = args.diarias.filter(d => d.tipo === "DESCONTO" || d.tipo === "ADIANTAMENTO");
  if (abatidos.length > 0) {
    linhas.push(`${MARCA_SECAO} Abatimentos ${MARCA_ITEM} − ${formatarBRL(descontos + adiantamentos)}`);
    for (const d of [...abatidos].sort((a, b) => a.data.localeCompare(b.data))) {
      const rotulo = d.descricao?.trim() || TIPO_LABEL[d.tipo] || d.tipo;
      linhas.push(`  ${MARCA_ITEM} ${formatarDataCurta(d.data)} — ${rotulo} — − ${formatarBRL(d.valor)}`);
    }
    linhas.push("");
  }

  // ─── Lançamentos avulsos (tarefa_adicional) ───────────────────────────────
  if (args.lancamentos.length > 0) {
    const sinalTotal = lancCentavos < 0 ? "− " : "+ ";
    linhas.push(`${MARCA_SECAO} Lançamentos ${MARCA_ITEM} ${sinalTotal}${formatarBRL(paraReais(Math.abs(lancCentavos)))}`);
    for (const l of [...args.lancamentos].sort((a, b) => a.data.localeCompare(b.data))) {
      const desconto = l.sinal === "DESCONTO";
      linhas.push(`  ${MARCA_ITEM} ${formatarDataCurta(l.data)} — ${l.descricao?.trim() || (desconto ? "desconto" : "adicional")} — ${desconto ? "−" : "+"} ${formatarBRL(l.valor)}`);
    }
    linhas.push("");
  }

  linhas.push(`${pago ? "TOTAL RECEBIDO" : "TOTAL A RECEBER"}: ${formatarBRL(paraReais(totalCentavos))}`);
  if (!pago) {
    linhas.push("Valores desta quinzena ainda em aberto — podem mudar até o fechamento.");
  }
  linhas.push("");
  linhas.push(`${args.organizacao} ${MARCA_ITEM} gerado em ${dataComAno(args.geradoEm)}`);

  return linhas.join("\n");
}

export interface ReciboMesArgs {
  organizacao: string;
  competencia: string; // 'YYYY-MM'
  /** Data em que o fechamento do mês é pago (`dataPagamentoDaCompetencia`). */
  dataPagamento: string;
  linha: LinhaMensalistaMes;
  geradoEm: string;
}

/**
 * Recibo do MENSALISTA/encarregado: o fechamento do mês.
 *
 * Os números vêm de `salarioLinhaMes`/`liquidoLinhaMes`, que já respeitam a
 * invariante do snapshot — com fechamento gerado, manda o que foi congelado, e
 * não o cadastro de hoje. Sem fechamento é PROJEÇÃO, e o texto diz isso.
 *
 * Os adicionais aparecem em bloco à parte porque não saem aqui: eles são pagos
 * na quinzena (D-M4), e cada um com a data em que saiu de verdade.
 */
export function montarReciboMes(args: ReciboMesArgs): string {
  const l = args.linha;
  const estado = estadoLinhaMes(l);
  const fechado = estado === "fechado";
  const temFechamento = !!l.fechamento;

  const salario = salarioLinhaMes(l);
  const adiantadoCentavos = adiantamentoLinhaMesCentavos(l);
  const variavel = l.fechamento?.variavel_apurada ?? "0";
  const descontosCentavos = temFechamento ? paraCentavos(l.fechamento!.descontos) : l.descontos_centavos;
  const liquido = liquidoLinhaMes(l);
  const situacao = situacaoAdiantamentoMes(l);

  const linhas: string[] = [];
  linhas.push(`${fechado ? "RECIBO" : "DEMONSTRATIVO"} ${MARCA_ITEM} ${nomeDe(l)}`);

  const regime = l.encarregado && l.setores_encarregado.length > 0
    ? `Encarregado (${l.setores_encarregado.join(", ")})`
    : (l.forma_pagamento ?? "MENSAL") === "QUINZENAL" ? "Mensalista" : "Mensalista (1x por mês)";
  linhas.push(`${formatarCompetencia(args.competencia)} ${MARCA_ITEM} ${regime}`);
  linhas.push(fechado
    ? `Fechamento pago em ${dataComAno(l.fechamento?.fechado_em ?? args.dataPagamento)}`
    : `Fechamento a pagar em ${dataComAno(args.dataPagamento)}`);
  linhas.push("");

  linhas.push(`${MARCA_SECAO} Fechamento do mês`);
  linhas.push(`  ${MARCA_ITEM} Salário: ${formatarBRL(salario)}`);
  if (adiantadoCentavos > 0) {
    const quando = situacao === "pago_fora"
      ? `pago por fora em ${formatarDataCurta(l.adiantamento?.pago_em)}`
      : situacao === "pago_quinzena"
        ? `pago com a quinzena de ${formatarDataCurta(l.adiantamento?.quinzena_data_pagamento ?? l.adiantamento?.quinzena_periodo_fim)}`
        : situacao === "previsto"
          ? `a pagar com a quinzena de ${formatarDataCurta(l.adiantamento?.quinzena_data_pagamento ?? l.adiantamento?.quinzena_periodo_fim)}`
          : "ainda não gerado";
    linhas.push(`  ${MARCA_ITEM} Adiantamento: − ${formatarBRL(paraReais(adiantadoCentavos))} (${quando})`);
  }
  if (paraCentavos(variavel) > 0) {
    linhas.push(`  ${MARCA_ITEM} Variável: + ${formatarBRL(variavel)}`);
  }
  if (descontosCentavos > 0) {
    linhas.push(`  ${MARCA_ITEM} Descontos: − ${formatarBRL(paraReais(descontosCentavos))}`);
    for (const d of l.lancamentos.filter(x => x.sinal === "DESCONTO")) {
      linhas.push(`      ${formatarDataCurta(d.data)} — ${d.descricao?.trim() || "desconto"} — − ${formatarBRL(d.valor)}`);
    }
  }
  linhas.push("");
  linhas.push(`${fechado ? "LÍQUIDO PAGO" : "LÍQUIDO A PAGAR"}: ${formatarBRL(liquido)}`);
  if (!temFechamento) {
    linhas.push("Projeção — o mês ainda não foi fechado, os valores podem mudar.");
  }
  linhas.push("");

  linhas.push(`${MARCA_SECAO} Presença no mês`);
  linhas.push(`  ${MARCA_ITEM} ${plural(l.dias_registro, "dia de trabalho", "dias de trabalho")}${l.dias_falta > 0 ? ` ${MARCA_ITEM} ${plural(l.dias_falta, "falta", "faltas")}` : ""}`);
  if (l.dias_falta > 0 && l.datas_falta.length > 0) {
    linhas.push(`      faltas: ${l.datas_falta.map(formatarDataCurta).join(", ")}`);
  }

  // Adicionais NÃO saem no fechamento: são pagos na quinzena (D-M4). Bloco
  // separado, com a data em que cada um saiu de verdade.
  const adicionais = l.lancamentos.filter(x => x.sinal !== "DESCONTO");
  if (adicionais.length > 0) {
    const totalAdicionais = netLancamentosCentavos(adicionais);
    const quando = textoPagamentoLancamentos(agruparLancamentosPorPagamento(adicionais));
    linhas.push("");
    linhas.push(`${MARCA_SECAO} Pago junto com as quinzenas (fora do fechamento)`);
    linhas.push(`  ${MARCA_ITEM} Adicionais: + ${formatarBRL(paraReais(totalAdicionais))}${quando ? ` (${quando})` : ""}`);
    for (const a of adicionais) {
      linhas.push(`      ${formatarDataCurta(a.data)} — ${a.descricao?.trim() || "adicional"} — + ${formatarBRL(a.valor)}`);
    }
  }

  linhas.push("");
  linhas.push(`${args.organizacao} ${MARCA_ITEM} gerado em ${dataComAno(args.geradoEm)}`);

  return linhas.join("\n");
}

/** Nome do arquivo/título curto — usado no assunto do compartilhamento. */
export function tituloRecibo(args: { pessoa: ReciboPessoa; periodo: string; pago: boolean }): string {
  return `${args.pago ? "Recibo" : "Demonstrativo"} ${MARCA_ITEM} ${nomeDe(args.pessoa)} ${MARCA_ITEM} ${args.periodo}`;
}

/** Só caracteres BMP sobrevivem ao WhatsApp Desktop — guarda testável. */
export function ehTextoBMP(texto: string): boolean {
  for (const ch of texto) {
    if ((ch.codePointAt(0) ?? 0) > 0xffff) return false;
  }
  return true;
}

export type { LinhaMensalistaMes, LancamentoDoMes };
